import * as fromActions from './schedule-rotation.actions';
import {IScheduleRotation, IShiftDetail, IWeek, WeekDays} from '@signal/asp-data-commons';
import {v4 as uuid} from 'uuid';
import {IScheduleRotationLabelId, ScheduleRotationState} from './schedule-rotation.reducer';
import {Draft} from 'immer';
import {extractDate} from '../../shared/services/util.service';

export function addWeek(action: fromActions.AddWeek, draftState: Draft<ScheduleRotationState>, state: ScheduleRotationState) {
  const department = action instanceof fromActions.AddWeek ? action.payload.department.toLowerCase() : '';
  const scheduleRotation = draftState[department].data[action.payload.id] as IScheduleRotation;
  // Generated by Copilot- Start
  const initialShiftDetail: IShiftDetail[] = scheduleRotation.shifts.map(shift => ({
    shiftId: shift.id,
    shiftName: shift.name,
    detail: []
  }));
  // Generated by Copilot- End
  const week = {
    [WeekDays.MONDAY]: initialShiftDetail,
    [WeekDays.TUESDAY]: initialShiftDetail,
    [WeekDays.WEDNESDAY]: initialShiftDetail,
    [WeekDays.THURSDAY]: initialShiftDetail,
    [WeekDays.FRIDAY]: initialShiftDetail,
    [WeekDays.SATURDAY]: initialShiftDetail,
    [WeekDays.SUNDAY]: initialShiftDetail,
  };
  scheduleRotation.rotation.push(week);
}

export function convertArrayToObject(scheduleRotations: IScheduleRotation[]) {
  const scheduleRotation: { [key: string]: IScheduleRotation } = {};
  scheduleRotations.forEach(sr => {
    scheduleRotation[sr.id] = sr;
  });
  return scheduleRotation;
}

export function addShift(action: fromActions.AddShift, draftState: Draft<ScheduleRotationState>, state: ScheduleRotationState) {
  const payload = action instanceof fromActions.AddShift ? action.payload : undefined;
  const department = payload.department.toLowerCase();
  const shiftId = uuid();
  const newShift = {...payload.shift, id: shiftId};
  // Generated by Copilot- Start
  draftState[department].data[action.payload.id].shifts.push(newShift);
  // Generated by Copilot- End
  /* populate each day of weeks with new shift */
  const rotation = (draftState[department].data[action.payload.id] as IScheduleRotation).rotation;
  rotation.forEach(r => {
    for (const day of Object.keys(r)) {
      r[day].push({shiftId, shiftName: newShift.name, detail: []});
    }
  });
}

export function updateShift(action: fromActions.UpdateShift, draftState: Draft<ScheduleRotationState>, state: ScheduleRotationState) {
  const payload = action instanceof fromActions.UpdateShift ? action.payload : undefined;
  const department = payload.department.toLowerCase();
  const shiftId = payload.updateShift.id as string;
  const scheduleRotation = (draftState[department].data[payload.id] as IScheduleRotation);
  // Generated by Copilot- Start
  /* Updating the shift by merging old and new shift object */
  const oldShift = scheduleRotation.shifts.find(s => s.id === shiftId);
  const newShift = {...oldShift, ...payload.updateShift.changes};
  const shiftIndex = scheduleRotation.shifts.findIndex(s => s.id === shiftId);
  scheduleRotation.shifts.splice(shiftIndex, 1, newShift);
  // Generated by Copilot- End
  /* populate each day of weeks with new shift */
  const rotation: IWeek[] = (draftState[department].data[action.payload.id] as IScheduleRotation).rotation;
  rotation.forEach(r => {
    for (const day of Object.keys(r)) {
      const shiftDetails: IShiftDetail[] = r[day];
      const shiftIndex = shiftDetails.findIndex(s => s.shiftId === shiftId);
      const updatedShift = {shiftId, shiftName: newShift.name, detail: shiftDetails[shiftIndex].detail};
      /* replace old shift with new one */
      shiftDetails.splice(shiftIndex, 1, updatedShift);
    }
  });
}

export function deleteShift(action: fromActions.DeleteShift, draftState: Draft<ScheduleRotationState>, state: ScheduleRotationState) {
  const payload = action instanceof fromActions.DeleteShift ? action.payload : undefined;
  const department = payload.department.toLowerCase();
  const shiftId = payload.shiftId;
  const scheduleRotation = (draftState[department].data[payload.id] as IScheduleRotation);
  // Generated by Copilot- Start
  const shiftIndex = scheduleRotation.shifts.findIndex(s => s.id === shiftId);
  scheduleRotation.shifts.splice(shiftIndex, 1);
  // Generated by Copilot- End
  /* populate each day of weeks with new shift */
  const rotation: IWeek[] = (draftState[department].data[action.payload.id] as IScheduleRotation).rotation;
  rotation.forEach(r => {
    for (const day of Object.keys(r)) {
      const shiftDetails: IShiftDetail[] = r[day];
      const shiftIndex = shiftDetails.findIndex(s => s.shiftId === shiftId);
      /* delete shift */
      shiftDetails.splice(shiftIndex, 1);
    }
  });
}

export function getActiveScheduleRotationLabels(scheduleRotations: IScheduleRotation[]) {
  const startDates = getSortedDates(scheduleRotations);
  const today = new Date(new Date().toISOString().substr(0, 10));
  const scheduleRotationLabels: IScheduleRotationLabelId = {inactiveIds: [], invalidIds: []} as IScheduleRotationLabelId;
  let currentlyActive;
  for (const date of startDates) {
    if (date.startDate <= today) {
      if (currentlyActive) {
        /* store prev currently active id as inactive */
        scheduleRotationLabels.inactiveIds.push(currentlyActive.id);
      }
      scheduleRotationLabels.activeId = date.sr.id;
      currentlyActive = date.sr;
    } else {
      /* store only first id as upcoming */
      if (!scheduleRotationLabels.upcomingId && date.startDate.getTime()) {
        scheduleRotationLabels.upcomingId = date.sr.id;
      }
      if(isNaN(date.startDate.getTime())){
        scheduleRotationLabels.invalidIds.push(date.sr.id);
      }
    }
  }
  return scheduleRotationLabels;
}

export function getAllOtherStartDates(scheduleRotations: IScheduleRotation[], id: string): string[] {
  return scheduleRotations.filter(sr => sr.id !== id).map(sr => sr.startDate);
}

export function orderScheduleRotationsByDate(scheduleRotations: IScheduleRotation[]) {
  const startDates = getSortedDates(scheduleRotations);
  return startDates.map(sd => sd.sr);
}

function getSortedDates(scheduleRotations: IScheduleRotation[]) {
  const startDates = scheduleRotations.map(sr => ({name: sr.name, startDate: new Date(sr.startDate), sr}));
  const today = extractDate();
  // const today = new Date(new Date().toISOString().substr(0, 10));
  startDates.sort((a, b) => {
    let diff;
    const isANaN = isNaN(a?.startDate.getTime());
    const isBNaN = isNaN(b?.startDate.getTime());
    if (isANaN && isBNaN) {
      diff = 0;
    } else if (isANaN) {
      diff = 1;
    } else if (isBNaN) {
      diff = -1;
    } else {
      const distanceA = a.startDate.getTime() - today.getTime();
      const distanceB = b.startDate.getTime() - today.getTime();
      diff = distanceA - distanceB;
    }
    return diff; // sort a before b when the distance is smaller
  });
  return startDates;
}

export function getShiftIdsSorted(scheduleRotation: IScheduleRotation) {
  // Generated by Copilot- Start
  const shifts = [...(scheduleRotation?.shifts || [])];
  shifts.sort((a, b) => a.fromTime.localeCompare(b.fromTime));
  return shifts.map(s => s.id);
  // Generated by Copilot- End
}

export function orderShiftsByTime(scheduleRotation: IScheduleRotation): IScheduleRotation {
  const shiftIds = getShiftIdsSorted(scheduleRotation);

  /* sorting shifts in each week day of each rotation */
  const rotations = [];
  scheduleRotation.rotation.forEach((r: IWeek) => {
    const newRotation = {};
    for (const day of Object.keys(r)) {
      const weekDay = [...(r[day] as IShiftDetail[])];
      weekDay.sort((a, b) => shiftIds.indexOf(a.shiftId) - shiftIds.indexOf(b.shiftId));
      newRotation[day] = weekDay;
    }
    rotations.push(newRotation);
  });
  return {...scheduleRotation, rotation: rotations};
}
